import { ReactElement, useContext, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { headerServicesMock } from '~/ui/components/common/Services/servicesMock';
import Services from '~/ui/components/common/Services';
import gsap from 'gsap';
import { useRouter } from 'next/router';
import { computedWithDiscount, computePrices } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { getPrices } from '@Hotels-at-Home/hah-fe-core/dist/utils/price';
import { CoreContext } from '@Hotels-at-Home/hah-fe-core/dist/context';
import classNames from 'classnames';
import { sitewideDiscount } from '~/constants/siteConfig';

// static prices in EUR
// we use discount to calculate the discount set on the sitewideDiscount constant
// we treat this as a percentage and will need to be updated accordingly for each promo
// if set to 0 no discount will be applied (this can be helpful when a promo is active but no discount is applied e.g. free shipping)
// if isPercentage is false it will treat the discount as a flat discount and just substract it from the price
// inside computedWithDiscount function we get the promo_is_active boolean so when a promotion starts and ends the discount will be applied automatically
// if it's a variable pricing promo and any individual product/category needs a different discount we should update it manually where it applies
const staticPrices = [
  // Bed category
  {
    maxRegular: 1040,
    minRegular: 1040,
    discount: 450,
    isPercentage: false,
  },
];

const HeroSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '' });
  const { t: tHome } = useTranslation('', { keyPrefix: '$pages.$home.$hero' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const router = useRouter();
  const wrapperRef = useRef(null);

  const {
    data: { currency, promotion },
  } = useContext(CoreContext);

  const prices = computedWithDiscount(staticPrices, promotion?.promo_is_active);
  const staticPrice = computePrices(prices)[0];

  const currentCurrency = currency;
  const price = getPrices(staticPrice[currentCurrency], router.locale);
  useEffect(() => {
    const updateHeight = () => {
      if (wrapperRef.current) {
        const height = wrapperRef.current.scrollHeight;

        gsap.set(':root', {
          '--hero-block-height': `${height - 56}px`,
        });
      }
    };

    setTimeout(() => {
      updateHeight();
    }, 100);
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [router]);
  return (
    <section
      id="hp-hero"
      className="hp-hero"
      data-event-name="bloc_interact"
      data-bloc-name={tHome('bedHeading')}
    >
      <Link href={tHome('link')} noClass noTabbing ariaLabel={`${tHome('bedHeading')}`}>
        <picture>
          <source
            srcSet="images/homePage/heroSection/hp-main-hero-novotel-mobile.webp"
            media="(max-width: 768px)"
          />
          <source
            srcSet="images/homePage/heroSection/hp-main-hero-novotel-mobile.jpg"
            media="(max-width: 768px)"
          />
          <source srcSet="images/homePage/heroSection/hp-main-hero-novotel-desktop.webp" />
          <img
            src="images/homePage/heroSection/hp-main-hero-novotel-desktop.jpg"
            alt="hero"
            width={1920}
            height={702}
            className="hero-image"
          />
        </picture>
        <div className="height-wrapper">
          <div className="heading-wrapper" ref={wrapperRef}>
            <div className="wrapper">
              <h1 className="title">
                <Trans
                  i18nKey="heading"
                  t={tHome}
                  components={{
                    span: <span />,
                  }}
                />
              </h1>
              <p className="description">{tHome('headingContent')}</p>
            </div>
            <div className="wrapper">
              <div className="hero-cta">
                <div className={classNames('price', currentCurrency.toLowerCase())}>
                  {tHome('from')} {price.newPrice}
                </div>
                <div className="cta">{tButton('shopNow')}</div>
              </div>
            </div>
          </div>
        </div>
      </Link>
      <div
        className="header-services"
        role="complementary"
        aria-label={t('$local.$services.ariaLabel')}
      >
        <Services items={headerServicesMock} />
      </div>
    </section>
  );
};

export default HeroSection;
